<script setup lang="ts">const originalUtterances = __MACROS_toRef(__props, "utterances");

import { toRef as __MACROS_toRef } from "vue";
/**
 * NOTE:
 * This component should be unique to each file. For unique selected ranges, styles, etc
 */
import Mousetrap from "mousetrap";
import { v4 as uuid } from "uuid";
import debounce from "lodash.debounce";
import { useRouter } from "vue-router";
import { ref, computed, watch, onMounted, onUnmounted, useTemplateRef, toRaw } from "vue";

import { useWindow } from "@/hooks/use-window";
import { useIsPopout } from "@/hooks/use-is-popout";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { getMarkStatus, isTranscriptAnnotation } from "@/services/UtilitiesService";
import { saveAnnotation } from "@/services/AnnotationService";

import { useAnnotationStore, useBrandStore, useCaseFileStore, useMessageStore, useVoiceMachineStore } from "@/stores";
import { useCaseSelectionMenuStore } from "@/components/SelectionMenu/case-selection-menu-store";

import VoiceTranscriptUtterances from "./VoiceTranscriptUtterances.vue";
import Tooltip from "@/components/Tooltip/Tooltip.vue";
import ConfirmAnnotationDelete from "@/components/ConfirmAnnotationDelete.vue";

import type { ParsedMark } from "@/models/Mark";
import type { Utterance } from "@/stores/case-file-store";
import type { CURSOR_MODE } from "./VoiceTranscriptFile.vue";
import type { TranscriptAnnotation } from "@/models/Annotation";
import type { ComponentExposed } from "vue-component-type-helpers";

const DRAG_HANDLE_IS_TOP_DATA_ATTR = "data-top";
const UTTERANCE_REDACTED_ID_ATTR = "data-redacted-id";
const CONTAINER_MODE_DATA_ATTR = "data-transcript-mode";
const UTTERANCE_INDEX_DATA_ATTR = "data-utterance-index";
const UTTERANCE_END_TIME_DATA_ATTR = "data-utterance-end-time";
const UTTERANCE_START_TIME_DATA_ATTR = "data-utterance-start-time";

withDefaults(defineProps<{
  fileReference: string;
  mode: CURSOR_MODE;
  utterances: Utterance[];
  isVisible: boolean | undefined;
  panelWidth: number | undefined;
}>(), {  })

defineSlots<{ panels(): void }>();

const $router = useRouter();

const window = useWindow();
const isPopout = useIsPopout();
const routeParams = useCaseRouteParams();

const container = ref<HTMLElement | null>(null);
const redactedAreasStylesheet = ref<CSSStyleSheet>();
const dragHandles = ref<{ top: number; bottom: number }>({ top: 0, bottom: 0 });

const isPanning = ref(false);
const isSavingMark = ref(false);
const isDraggingHandles = ref(false);

const activeCopies = ref<{ state: "success" | "failure"; copyId: string }>();

const isMounted = ref(false);
const isProgrammaticScroll = ref(false);
const jumpTo = ref<{
  source: "time-range" | "time" | "annotation" | "action";
  startTime: number;
  endTime?: number;
  handled?: true;
  utteranceIndexOffset?: number;
  id?: string;
}>();

const utteranceRenderer = useTemplateRef<ComponentExposed<typeof VoiceTranscriptUtterances>>("utterance-renderer");
const selectedRange = ref<{ startTime: number; endTime: number }>();

const showHandles = computed(
  () =>
    selectedAnnotation.value?.fileReference === __props.fileReference &&
    !/rejected/i.test(getMarkStatus(selectedAnnotation.value)) &&
    selectedRange.value != null,
);

const evidenceHighlightColors = computed(() => useBrandStore().viewerConfig.evidenceThemes?.highlightColors ?? {});

const selectedThemes = computed(() => {
  const selected = useCaseFileStore().selectedThemes;
  if (selected == null) return;

  const result = new Set<string>();
  const hiddenThemes = useBrandStore().viewerConfig.evidenceThemes?.hiddenThemes ?? {};

  for (const theme of selected) {
    if (hiddenThemes[theme] === true) continue;
    result.add(theme);
  }

  return result;
});

const userAnnotations = computed(() => {
  const alwaysShow = ["user", "accepted"];

  return useAnnotationStore()
    .annotations.filter(
      (mark): mark is ParsedMark<TranscriptAnnotation> =>
        mark.fileReference === __props.fileReference &&
        isTranscriptAnnotation(mark.parsedMarkData) &&
        alwaysShow.includes(getMarkStatus(mark)),
    )
    .sort((a, b) => a.parsedMarkData.startTime - b.parsedMarkData.startTime);
});

const themesByUtterance = computed(() => {
  const allThemes = new Set<string>();
  const result = new Map<number, string[]>();

  const allAnnotations = useAnnotationStore()
    .annotations.filter(
      (mark): mark is ParsedMark<TranscriptAnnotation> =>
        mark.fileReference === __props.fileReference && isTranscriptAnnotation(mark.parsedMarkData),
    )
    .sort((a, b) => a.parsedMarkData.startTime - b.parsedMarkData.startTime);

  let annotationStartIdx = 0;
  for (let utteranceIdx = 0; utteranceIdx < __props.utterances.length; utteranceIdx++) {
    const utterance = __props.utterances[utteranceIdx];

    // Advance past annotations that end before this utterance starts
    while (
      annotationStartIdx < allAnnotations.length &&
      allAnnotations[annotationStartIdx].parsedMarkData.endTime <= utterance.startTime
    ) {
      annotationStartIdx++;
    }

    // Collect all annotations that overlap with this utterance
    const themes = new Set<string>();
    let annotationEndIdx = annotationStartIdx;
    while (
      annotationEndIdx < allAnnotations.length &&
      allAnnotations[annotationEndIdx].parsedMarkData.startTime < utterance.endTime
    ) {
      const annotation = allAnnotations[annotationEndIdx];

      annotationEndIdx++;

      annotation.evidenceThemes?.forEach((e) => {
        if (selectedThemes.value != null && !selectedThemes.value.has(e.evidenceTheme)) return;

        themes.add(e.evidenceTheme);
        allThemes.add(e.evidenceTheme);
      });
    }

    if (themes.size > 0) result.set(utteranceIdx, [...themes]);
  }

  return {
    result,
    themesMaxCount: allThemes.size,
    allThemes: [...allThemes]
      .sort((a, b) => a.localeCompare(b))
      .reduce(
        (acc, theme, index) => {
          acc[theme] = index;
          return acc;
        },
        {} as Record<string, number>,
      ),
  };
});

const selectedAnnotation = computed(() => {
  if (routeParams.value.annotation == null) return;

  const annotation = useAnnotationStore().annotations.find((a) => a.markId === routeParams.value.annotation);
  if (!isTranscriptAnnotation(annotation?.parsedMarkData)) return;

  return annotation as ParsedMark<TranscriptAnnotation>;
});

const onScrollEnd = debounce(() => (isProgrammaticScroll.value = false), 250);

watch(
  userAnnotations,
  () => {
    const redactedAreas = userAnnotations.value.flatMap((mark) => {
      if (mark.parsedMarkData.type !== "TranscriptRedaction") return [];
      return mark.parsedMarkData.uuid;
    });

    if (redactedAreas.length < 1) return;

    if (redactedAreasStylesheet.value == null) {
      redactedAreasStylesheet.value = new CSSStyleSheet();
      window.value.document.adoptedStyleSheets.push(redactedAreasStylesheet.value);
    }

    const redactedAreasSelector = redactedAreas.reduce<Record<"hover" | "idleChildren", string[]>>(
      (acc, area) => {
        const attrSelector = `[${UTTERANCE_REDACTED_ID_ATTR}="${area}"]`;
        return {
          hover: [...acc.hover, `html:has(${attrSelector}:hover) ${attrSelector}`],
          idleChildren: [...acc.idleChildren, `html:has(${attrSelector}:not(:hover)) ${attrSelector} > *`],
        };
      },
      { hover: [], idleChildren: [] },
    );

    redactedAreasStylesheet.value.replaceSync(`
    ${redactedAreasSelector.hover.join(",")} { --tw-bg-opacity: 0.5 !important; }
    ${redactedAreasSelector.idleChildren.join(",")} { color: #000 !important; }
    `);
  },
  { immediate: true },
);

watch(
  () => __props.mode,
  (newMode) => {
    if (isPopout) return;

    // If the new mode is not MARK or an annotation is selected, no need to reset the URL
    if (newMode !== "CREATE-MARK" || selectedRange.value == null) return;

    const { brand, casename, casefile, annotation } = routeParams.value;
    // If an annotation in the file is selected, no need to reset the URL
    if (casefile !== __props.fileReference || annotation != null) return;

    selectedRange.value = undefined;
    $router.push({ name: "casefile", params: { brand, casename, casefile } });
  },
);

watch(
  [() => __props.isVisible, isMounted, jumpTo],
  ([newIsVisible, newIsMounted, newJumpTo], _, onCleanup) => {
    // Only when the tab is active, component mounted, and the jump hasn't been handled
    if (
      !newIsMounted ||
      !newIsVisible ||
      newJumpTo == null ||
      newJumpTo.handled === true ||
      __props.utterances.length === 0
    )
      return;

    const { startTime, endTime, utteranceIndexOffset = 0 } = newJumpTo;

    if (endTime != null && startTime > endTime) {
      if (!isPopout) {
        $router.push({
          name: "casefile",
          params: {
            brand: routeParams.value.brand,
            casename: routeParams.value.casename,
            casefile: __props.fileReference,
          },
        });
      }

      return;
    }

    // If the start time is less than the first utterance, select the first element
    const startBlockIdx =
      startTime <= __props.utterances[0].startTime
        ? 0
        : __props.utterances.findLastIndex((el) => el.startTime <= startTime);
    const startBlock = __props.utterances[startBlockIdx];

    // Set the selected blocks to match the time range given, if an annotation has already been selected
    if (newJumpTo.source === "annotation" || selectedAnnotation.value == null) {
      if (endTime != null) selectedRange.value = { startTime, endTime };
      else selectedRange.value = { startTime: startBlock.startTime, endTime: startBlock.endTime };
    }

    const frameId = requestAnimationFrame(() => {
      if (utteranceRenderer.value == null) return;

      isProgrammaticScroll.value = true;
      utteranceRenderer.value.scrollToIndex(startBlockIdx + utteranceIndexOffset /* , "center" */);
      onScrollEnd();

      newJumpTo.handled = true;
    });

    onCleanup(() => cancelAnimationFrame(frameId));
  },
  { flush: "post" },
);

watch(
  [routeParams, selectedAnnotation],
  ([{ endTime, startTime, casefile, time }]) => {
    if (casefile !== __props.fileReference) return;

    if (time != null) jumpTo.value = { startTime: +time, source: "time" };
    else if (startTime != null) {
      jumpTo.value = {
        startTime: +startTime,
        endTime: endTime == null ? undefined : +endTime,
        source: "time-range",
      };
    } else if (
      selectedAnnotation.value?.fileReference === __props.fileReference &&
      jumpTo.value?.id !== selectedAnnotation.value.markId
    ) {
      const { startTime, endTime, utteranceList } = selectedAnnotation.value.parsedMarkData;

      const utteranceIndexOffset = utteranceList.findIndex((utterance) => !utterance.isContext);

      jumpTo.value = {
        id: selectedAnnotation.value.markId,
        startTime,
        endTime,
        utteranceIndexOffset: utteranceIndexOffset === -1 ? undefined : utteranceIndexOffset,
        source: "annotation",
      };
    }
  },
  { immediate: true },
);

watch(
  [() => __props.panelWidth, selectedRange],
  () => {
    if (isDraggingHandles.value || selectedRange.value == null) return;
    const range = selectedRange.value;
    // requestAnimationFrame and vue's nextTick don't give enough of a delay to ensure the item is settled
    setTimeout(() => calculateHandleStyles(range));
  },
  { flush: "post" },
);

useVoiceMachineStore().$onAction(({ name, args }) => {
  if (args[1] !== __props.fileReference) return;
  if (name === "setTimeRange" && args[0] !== "transcript")
    jumpTo.value = { startTime: args[2], endTime: args[3], utteranceIndexOffset: 3, source: "action" };
});

onMounted(() => {
  isMounted.value = true;

  Mousetrap.bind("esc", () => {
    if (isPopout) return;

    const { annotation, brand, casename, casefile } = routeParams.value;
    if (annotation == null || casefile !== __props.fileReference) return;

    selectedRange.value = undefined;
    $router.push({ name: "casefile", params: { brand, casename, casefile } });
  });
});

defineExpose({ container });

let copyDismissTimerId: ReturnType<typeof setTimeout> | undefined;
let dismissSelection: (() => void) | undefined;
let stopSelectionPointerUpTracking: (() => void) | undefined;

onUnmounted(() => {
  dismissSelection?.();
  clearTimeout(copyDismissTimerId);
  stopSelectionPointerUpTracking?.();
});

watch([() => __props.mode, () => __props.isVisible], () => dismissSelection?.());

function onSelectToolEnd() {
  if (__props.mode !== "SELECT" || routeParams.value.casefile !== __props.fileReference || container.value == null) return;

  const selection = window.value.document.getSelection();
  // Ensure text is selected
  if (selection == null || selection.rangeCount === 0 || selection.isCollapsed) return;

  const range = selection.getRangeAt(0).cloneRange();

  // Ensure selected text is in transcript container
  if (!container.value.contains(range.commonAncestorContainer)) {
    useMessageStore().updateMessage({
      type: "warning",
      title: "Selection not copied",
      message: "Keep the whole selection within this transcript to copy it.",
      autoHide: true,
      showReload: false,
    });

    return;
  }

  // Copy Selection
  (() => {
    clearTimeout(copyDismissTimerId);
    navigator.clipboard
      .writeText(selection.toString())
      .then(
        () => (activeCopies.value = { state: "success", copyId: Math.random().toString(36) }),
        () => (activeCopies.value = { state: "failure", copyId: Math.random().toString(36) }),
      )
      .then(
        (activeCopy) =>
          void (copyDismissTimerId = setTimeout(() => {
            if (toRaw(activeCopies.value) !== toRaw(activeCopy)) return;
            activeCopies.value = undefined;
          }, 1e3)),
      );
  })();

  // Publish Selection
  (() => {
    const startRow = (
      range.startContainer instanceof Element ? range.startContainer : range.startContainer.parentElement
    )?.closest<HTMLElement>(`[${UTTERANCE_INDEX_DATA_ATTR}]`);
    const endRow = (
      range.endContainer instanceof Element ? range.endContainer : range.endContainer.parentElement
    )?.closest<HTMLElement>(`[${UTTERANCE_INDEX_DATA_ATTR}]`);
    if (startRow == null || endRow == null) return;

    const startParagraph = startRow.querySelector("[data-type=utterance]")!;
    const startIdx = +startRow.getAttribute(UTTERANCE_INDEX_DATA_ATTR)!;

    const endParagraph = endRow.querySelector("[data-type=utterance]")!;
    const endIdx = +endRow.getAttribute(UTTERANCE_INDEX_DATA_ATTR)!;

    const { startOffset, endOffset } = (() => {
      // Characters of `paragraph`'s text before the boundary point, clamped to the paragraph.
      const textOffset = (paragraph: Element, node: Node, offset: number) => {
        const contents = paragraph.ownerDocument.createRange();
        contents.selectNodeContents(paragraph);

        const position = contents.comparePoint(node, offset);

        if (position === -1) return 0;
        if (position === 1) return paragraph.textContent.length;

        contents.setEnd(node, offset);
        return contents.toString().length;
      };

      return {
        startOffset: textOffset(startParagraph, range.startContainer, range.startOffset),
        endOffset: textOffset(endParagraph, range.endContainer, range.endOffset),
      };
    })();
    let first: number | undefined;
    let last: number | undefined;
    const utteranceList: TranscriptAnnotation["utteranceList"] = [];

    for (let idx = startIdx; idx <= endIdx; idx++) {
      const { dialogueText, speaker, isContext } = __props.utterances[idx];

      const from = idx === startIdx ? startOffset : 0;
      const to = idx === endIdx ? endOffset : dialogueText.length;

      const selected = dialogueText.slice(from, to);
      if (selected.trim() === "") continue;

      last = idx;
      first ??= idx;

      utteranceList.push({
        speaker,
        isContext,
        dialogueText: `${from > 0 ? "..." : ""}${selected}${to < dialogueText.length ? "..." : ""}`,
      });
    }

    if (first == null || last == null) return;

    const startUtterance = __props.utterances[first];
    const endUtterance = __props.utterances[last];
    const document = window.value.document;

    const { id } = useCaseSelectionMenuStore().publish({
      source: {
        kind: "transcript",
        fileReference: __props.fileReference,
        startTime: startUtterance.startTime,
        endTime: endUtterance.endTime,
        utteranceList,
      },
      anchor: {
        getBoundingClientRect: () => range.getBoundingClientRect(),
        contextElement: container.value ?? undefined,
      },
      onDismiss: () => document.getSelection()?.removeAllRanges(),
    });
    dismissSelection = () => useCaseSelectionMenuStore().dismiss(id);
  })();
}

function onMouseDown(event: MouseEvent) {
  if (isPopout || event.button !== 0) return;

  const { brand, casename, casefile } = routeParams.value;
  if (__props.fileReference !== casefile) return;

  const utteranceIndex = Number(
    (event.target as HTMLElement).closest(`[${UTTERANCE_INDEX_DATA_ATTR}]`)?.getAttribute(UTTERANCE_INDEX_DATA_ATTR) ??
      NaN,
  );
  if (Number.isNaN(utteranceIndex)) return;

  const { startTime, endTime } = __props.utterances[utteranceIndex];
  if (startTime < 0 || endTime < 0) return;

  if (__props.mode === "SELECT") {
    stopSelectionPointerUpTracking?.();
    window.value.document.addEventListener("pointerup", onSelectToolEnd, { once: true });
    stopSelectionPointerUpTracking = () => window.value.document.removeEventListener("pointerup", onSelectToolEnd);
    return;
  }

  const blockMark = useAnnotationStore().annotations.find((mark) => {
    try {
      if (mark.fileReference !== casefile) return false;

      const status = getMarkStatus(mark);
      if (status !== "user" && status !== "accepted") return false;

      const markData = mark.parsedMarkData;
      if (!isTranscriptAnnotation(markData)) return false;

      return startTime >= markData.startTime && endTime <= markData.endTime;
    } catch (error) {
      console.error(error);
      return false;
    }
  });

  if (isTranscriptAnnotation(blockMark?.parsedMarkData)) {
    $router.push({
      name: "casefileannotation",
      params: { brand, casename, casefile, annotation: blockMark.parsedMarkData.uuid },
    });

    selectedRange.value = { startTime: blockMark.parsedMarkData.startTime, endTime: blockMark.parsedMarkData.endTime };
  } else if (__props.mode === "CREATE-MARK" || __props.mode === "REDACT") {
    selectedRange.value = { startTime, endTime };
    saveMark(uuid(), __props.mode === "REDACT");
  } else {
    $router.push({ name: "casefiletime", params: { brand, casename, casefile, time: +startTime.toFixed(2) } });
    useVoiceMachineStore().setTimeRange("transcript", casefile, startTime);
  }
}

function calculateHandleStyles(range: NonNullable<typeof selectedRange.value>) {
  if (!showHandles.value || container.value == null || utteranceRenderer.value == null) return;

  const startElementIdx = __props.utterances.findLastIndex((el) => +el.startTime === range.startTime);
  const endElementIdx = __props.utterances.findIndex((el) => +el.endTime === range.endTime);

  if (startElementIdx < 0 || endElementIdx < 0) {
    useMessageStore().updateMessage({
      message: "Could not locate the annotation's utterances",
      type: "error",
      title: "Transcript Utterances",
      autoHide: true,
      showReload: false,
    });

    return;
  }

  const containerY = container.value.scrollTop;
  dragHandles.value.top = (utteranceRenderer.value.getItemOffset(startElementIdx) ?? 0) + containerY;
  dragHandles.value.bottom = (utteranceRenderer.value.getItemOffset(endElementIdx + 1) ?? 0) + containerY;
}

function startMarkHandleDragging(event: MouseEvent) {
  const isTopHandle =
    (event.target as HTMLElement)
      .closest(`[${DRAG_HANDLE_IS_TOP_DATA_ATTR}]`)
      ?.getAttribute(DRAG_HANDLE_IS_TOP_DATA_ATTR) === "true";

  isDraggingHandles.value = true;

  function doDragging(ev: MouseEvent) {
    if (container.value == null || utteranceRenderer.value == null) return;

    const containerRect = container.value.getBoundingClientRect();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const renderedUtteranceEls = Object.values(utteranceRenderer.value.renderedUtteranceRefs).filter(
      (el): el is HTMLElement => el != null,
    );

    const containerY = -containerRect.top + container.value.scrollTop;
    const dragY = ev.clientY + containerY;

    let endY = dragHandles.value.bottom;
    let startY = dragHandles.value.top;

    if (isTopHandle) {
      startY = Math.max(0, Math.min(dragY, dragHandles.value.bottom));
      dragHandles.value.top = startY;

      const startBlock = renderedUtteranceEls.find((block) => {
        const { top, height } = block.getBoundingClientRect();
        return top + containerY >= startY - height * 0.5;
      });

      const currentEndTime = selectedRange.value?.endTime;
      const blockStartTime = startBlock?.getAttribute(UTTERANCE_START_TIME_DATA_ATTR);
      const blockEndTime = startBlock?.getAttribute(UTTERANCE_END_TIME_DATA_ATTR);

      if (
        blockStartTime == null ||
        blockEndTime == null ||
        (currentEndTime != null && +blockStartTime > currentEndTime)
      )
        return;

      selectedRange.value = { startTime: +blockStartTime, endTime: +(currentEndTime ?? blockEndTime) };
    } else {
      endY = Math.min(container.value.scrollHeight, Math.max(dragY, dragHandles.value.top));
      dragHandles.value.bottom = endY;

      const endBlock = renderedUtteranceEls.findLast((block) => {
        const { bottom, height } = block.getBoundingClientRect();
        return bottom + containerY <= endY + height * 0.5;
      });

      const currentStartTime = selectedRange.value?.startTime;
      const blockStartTime = endBlock?.getAttribute(UTTERANCE_START_TIME_DATA_ATTR);
      const blockEndTime = endBlock?.getAttribute(UTTERANCE_END_TIME_DATA_ATTR);

      if (
        blockStartTime == null ||
        blockEndTime == null ||
        (currentStartTime != null && +blockEndTime < currentStartTime)
      )
        return;

      selectedRange.value = { startTime: +(currentStartTime ?? blockStartTime), endTime: +blockEndTime };
    }
  }

  function stopDragging() {
    window.value.document.removeEventListener("mousemove", doDragging);
    window.value.document.removeEventListener("mouseup", stopDragging);

    if (selectedAnnotation.value != null) {
      saveMark(
        selectedAnnotation.value.markId,
        selectedAnnotation.value.parsedMarkData.type === "TranscriptRedaction",
      ).finally(() => {
        isDraggingHandles.value = false;
      });
    }

    if (selectedRange.value != null) calculateHandleStyles(selectedRange.value);
  }

  window.value.document.addEventListener("mousemove", doDragging);
  window.value.document.addEventListener("mouseup", stopDragging);
}

function handleScrolling() {
  if (selectedRange.value != null) {
    const range = selectedRange.value;
    setTimeout(() => calculateHandleStyles(range));
  }

  if (!isProgrammaticScroll.value) {
    useVoiceMachineStore().setShouldTrackAudio("transcript", __props.fileReference, false);
    jumpTo.value = undefined;
  }
}

async function saveMark(markId: string, redact: boolean) {
  if (isPopout) return;

  if (isSavingMark.value) {
    useMessageStore().updateMessage({
      type: "error",
      title: "Please Wait",
      message: "A Mark is currently being saved. Please wait before making another update.",
      autoHide: true,
      showReload: false,
    });

    return;
  }

  const { brand, casename, casefile } = routeParams.value;
  if (selectedRange.value == null || __props.fileReference !== casefile) return;

  const { startTime, endTime } = selectedRange.value;

  const selectedUtterances = __props.utterances
    .filter((utterance): utterance is Utterance => utterance.startTime >= startTime && utterance.endTime <= endTime, [])
    .map((utterance, idx) => ({
      order: idx,
      isContext: false,
      speaker: utterance.speaker,
      dialogueText: utterance.dialogueText,
    }));

  const startUtteranceIdx = __props.utterances.findIndex((utterance) => {
    if (utterance.startTime == null || utterance.endTime == null) return false;
    return utterance.startTime >= startTime && utterance.endTime <= endTime;
  });

  const endUtteranceIdx = startUtteranceIdx + selectedUtterances.length - 1;

  if (redact) {
    const overlappingRedaction = useAnnotationStore().annotations.find(
      (mark): mark is ParsedMark<TranscriptAnnotation> => {
        if (mark.fileReference !== casefile || mark.markId === markId) return false;

        const status = getMarkStatus(mark);
        if (status !== "user" && status !== "accepted") return false;

        const markData = mark.parsedMarkData;
        if (markData?.type !== "TranscriptRedaction") return false;

        return markData.startTime >= startTime && markData.endTime <= endTime;
      },
    );

    if (overlappingRedaction != null) {
      // Revert to original
      if (selectedAnnotation.value?.markId === markId) {
        selectedRange.value = {
          startTime: selectedAnnotation.value.parsedMarkData.startTime,
          endTime: selectedAnnotation.value.parsedMarkData.endTime,
        };
      }

      useMessageStore().updateMessage({
        type: "error",
        title: "Cannot save redaction",
        message: "Overlapping redactions are not allowed. Adjust the selection and try again.",
        autoHide: true,
        showReload: false,
      });

      return;
    }
  }

  const markData: TranscriptAnnotation = {
    type: redact ? "TranscriptRedaction" : "TranscriptAnnotation",
    uuid: markId,
    endTime,
    startTime,
    startUtterance: startUtteranceIdx,
    endUtterance: endUtteranceIdx,
    utteranceList: selectedUtterances,
  };

  const { status, metadata, source, evidenceThemes } =
    selectedAnnotation.value?.markId === markId ? selectedAnnotation.value : { source: "user" };

  try {
    isSavingMark.value = true;

    await saveAnnotation({
      markId,
      status,
      source,
      metadata,
      evidenceThemes,
      fileReference: casefile,
      supportingFileEntryFileReference: "",
      markData: JSON.stringify(markData),
      parsedMarkData: markData,
    });
  } finally {
    isSavingMark.value = false;
  }

  $router.push({
    name: "casefileannotation",
    params: { brand, casename, casefile, annotation: markData.uuid },
  });
}

function getUtteranceProps({ startTime, endTime }: Record<"startTime" | "endTime", number>, index: number) {
  const isSelectedStart = selectedRange.value?.startTime === startTime;
  const isSelectedEnd = selectedRange.value?.endTime === endTime;
  const isSelected =
    selectedRange.value == null
      ? false
      : startTime >= selectedRange.value.startTime && endTime <= selectedRange.value.endTime;
  const colors = {
    redaction: "tw-bg-black/40",
    ai: "tw-bg-[#E5DEDE]/40",
    rejected: "tw-bg-[#FC8181]/40",
    user: "tw-bg-[#FFFF00]/40",
    accepted: "tw-bg-[#FFFF00]/40",
    candidate: "",
  };
  const annotation = userAnnotations.value.find(
    (a) => startTime >= a.parsedMarkData.startTime && endTime <= a?.parsedMarkData.endTime,
  );

  const isRedacted = annotation?.parsedMarkData.type === "TranscriptRedaction";

  let bgClass: string | undefined;

  if (isRedacted) {
    // When the utterance is in a redacted area
    if (isSelected) {
      bgClass = colors.redaction;
    } else if (annotation != null && annotation.parsedMarkData?.uuid === selectedAnnotation.value?.markId) {
      // If this utterance is in the redaction but isn't in the selected range, it's being removed and should be shown clear
      bgClass = undefined;
    } else {
      bgClass = colors.redaction;
    }
  } else if (isSelected && selectedAnnotation.value != null) {
    // When the utterance is selected and has a specific status
    if (selectedAnnotation.value.parsedMarkData.type === "TranscriptRedaction") {
      bgClass = colors.redaction;
    } else {
      bgClass = colors[getMarkStatus(selectedAnnotation.value)];
    }
  } else if (isSelected && $router.currentRoute.value.name === "casefiletimerange") {
    // Are we selecting a range from a source?
    bgClass = colors.ai;
  } else if (annotation != null && annotation.parsedMarkData?.uuid !== selectedAnnotation.value?.markId) {
    // Fallback to user annotation
    bgClass = colors.user;
  } else {
    bgClass = undefined;
  }

  return {
    [UTTERANCE_INDEX_DATA_ATTR]: index,
    [UTTERANCE_END_TIME_DATA_ATTR]: endTime.toFixed(2),
    [UTTERANCE_START_TIME_DATA_ATTR]: startTime.toFixed(2),
    [UTTERANCE_REDACTED_ID_ATTR]: isRedacted ? annotation.parsedMarkData.uuid : undefined,
    class: [
      "tw-transition-[background-color]",
      bgClass ?? "tw-bg-white",
      isSelectedStart && "tw-border-t tw-border-t-black",
      isSelectedEnd && "tw-border-b tw-border-b-black",
      isDraggingHandles.value && "tw-border-dashed",
      !isRedacted && "hover:tw-brightness-90",
    ]
      .filter(Boolean)
      .join(" "),
  };
}

function onAnnotationDeleted() {
  // Reload Annotations
  useAnnotationStore().load();
  selectedRange.value = undefined;
}
</script>
<template>
  <div
    class="tw-relative tw-flex tw-size-full tw-min-h-0 tw-min-w-0 tw-grow tw-items-center tw-justify-start tw-bg-[#e7e9ef] tw-text-[0.8rem]"
    :[CONTAINER_MODE_DATA_ATTR]="mode"
    @mousedown="onMouseDown"
  >
    <slot name="panels" />
    <div
      ref="container"
      :style="{ '--themes-count-max': themesByUtterance.themesMaxCount }"
      class="tw-relative tw-size-full [--theme-bar-width:theme(spacing.2)]"
      :class="
        isPanning
          ? 'tw-cursor-grabbing'
          : mode === 'SELECT'
            ? 'tw-cursor-text'
            : mode === 'PAN'
              ? 'tw-cursor-grab tw-select-none'
              : mode === 'CREATE-MARK' || mode === 'REDACT'
                ? 'tw-cursor-move tw-select-none'
                : undefined
      "
    >
      <VoiceTranscriptUtterances
        ref="utterance-renderer"
        virtualize-rendering
        :list="originalUtterances"
        :file-reference="fileReference"
        :get-utterance-props="getUtteranceProps"
        @scroll="handleScrolling"
      >
        <template v-if="themesByUtterance.themesMaxCount > 0" #beforeUtterance="{ index }">
          <div
            aria-hidden="true"
            class="tw-grid tw-cursor-default tw-grid-flow-col tw-grid-cols-[repeat(var(--themes-count-max),var(--theme-bar-width))] tw-gap-0.5 tw-px-0.5"
            @click.stop
          >
            <Tooltip
              v-for="theme in themesByUtterance.result.get(index)"
              :key="theme"
              :content-props="{ side: 'left' }"
              :root-props="{ delayDuration: 0 }"
            >
              <template #trigger>
                <div
                  class="tw-col-start-[--col-start] tw-size-full tw-bg-[--color]"
                  :style="{
                    '--col-start': themesByUtterance.allThemes[theme] + 1,
                    '--color': evidenceHighlightColors[theme] ?? '#ffff00',
                  }"
                />
              </template>
              <template #content>{{ theme }}</template>
            </Tooltip>
          </div>
        </template>
      </VoiceTranscriptUtterances>
      <template v-if="showHandles">
        <div
          v-for="(value, side) in dragHandles"
          :key="side"
          class="tw-absolute tw-inset-x-0 tw-z-0 tw-h-0.5"
          :style="{ top: `${value}px` }"
          :class="{ 'tw-bg-black': isDraggingHandles }"
          :[DRAG_HANDLE_IS_TOP_DATA_ATTR]="side === 'top'"
        >
          <button
            type="button"
            class="tw-absolute tw-left-1/2 tw-top-1/2 tw-z-10 tw-h-2.5 tw-w-2.5 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-cursor-ns-resize tw-rounded-full tw-bg-blue-500 tw-shadow"
            @mousedown="startMarkHandleDragging"
          />
        </div>
      </template>
    </div>
    <span
      v-if="activeCopies != null"
      :key="activeCopies.copyId"
      class="tw-fixed tw-left-1/2 tw-top-10 tw-z-[1000] tw-flex -tw-translate-x-1/2 tw-items-center tw-justify-start tw-gap-1 tw-rounded-md tw-bg-white tw-p-2 tw-shadow"
    >
      <template v-if="activeCopies.state === 'success'">
        <i class="fas fa-circle-check" />
        Copied to Clipboard!
      </template>
      <template v-else>
        <i class="fas fa-circle-xmark" />
        Error Copying to Clipboard
      </template>
    </span>
    <ConfirmAnnotationDelete @annotation-deleted="onAnnotationDeleted" />
  </div>
</template>
